import { RouterView } from 'vue-router';
import type { RouteRecordRaw, RouteLocation } from 'vue-router';
import { useI18n } from '@/app/modules/i18n';
import { useAccountStore } from '@/app/modules/account';
import { useSupersetKeyMeta, useSupersetDashboardsStore } from '@/app/modules/superset';
import { FeatureFlag } from '@/app/modules/feature-flags';
import { ModuleNames } from '@/app/shared/module-names';
import {
  authGuard,
  notAuthGuard,
  createFeatureFlagGuard,
  createNotElephantGuard,
  elephantGuard,
  specialEventsEntryGuard,
} from '../guards';
import { RoutesNames } from '../route-names';

const legacySpecialEventsGuard = createNotElephantGuard('/special-events');

async function getSupersetPageTitle(route: RouteLocation) {
  const store = useSupersetDashboardsStore();
  const { getTextForKey } = useSupersetKeyMeta();

  await store.fetch();

  const dashboard = store.dashboards.find(({ uuid }) => uuid === route.params.id);

  if (dashboard) {
    return getTextForKey(dashboard.name);
  }

  return null;
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/app/layouts/default-layout.vue'),
    meta: {
      guards: [authGuard],
    },
    children: [
      {
        path: '',
        component: () => import('@/app/modules/home/views/home-page-entry.vue'),
        meta: {
          guards: [createFeatureFlagGuard(FeatureFlag.HomePage, '/inventory')],
          title: () => {
            const { t } = useI18n();
            return t('messages.home');
          },
        },
      },
      {
        path: '/replenishment',
        component: RouterView,
        redirect: '/replenishment/store',
        children: [
          {
            path: ':locationType',
            component: () => import('@/app/modules/replenishment/views/replenishment-index.vue'),
            props: (route) => ({ locationType: route.params.locationType }),
            redirect: (route) => `/replenishment/${route.params.locationType}/overview`,
            meta: {
              guards: [
                (to) => {
                  if (to.params.locationType === 'warehouse') {
                    const store = useAccountStore();
                    return store.whReplenishmentActive || '/replenishment';
                  }

                  if (to.params.locationType !== 'store') {
                    return '/replenishment';
                  }

                  return true;
                },
              ],
            },
            children: [
              {
                path: 'overview',
                component: () => import('@/app/modules/replenishment/views/replenishment-overview.vue'),
                props: (route) => ({ locationType: route.params.locationType }),
                meta: {
                  trackVisit: true,
                  title: (route) => {
                    const { t } = useI18n();
                    return route.params.locationType === 'warehouse'
                      ? t('modules.replenishment.messages.whReplenishmentOverview')
                      : t('modules.replenishment.messages.storeReplenishmentOverview');
                  },
                  moduleName: ModuleNames.Replenishment,
                },
              },
              {
                path: 'locations',
                component: () => import('@/app/modules/replenishment/views/replenishment-locations.vue'),
                props: (route) => ({ locationType: route.params.locationType }),
                meta: {
                  trackVisit: true,
                  title: (route) => {
                    const { t } = useI18n();
                    return route.params.locationType === 'warehouse'
                      ? t('modules.replenishment.messages.whReplenishmentLocations')
                      : t('modules.replenishment.messages.storeReplenishmentLocations');
                  },
                  moduleName: ModuleNames.Replenishment,
                },
              },
              {
                path: 'products',
                component: () => import('@/app/modules/replenishment/views/replenishment-products.vue'),
                props: (route) => ({ locationType: route.params.locationType }),
                meta: {
                  trackVisit: true,
                  title: (route) => {
                    const { t } = useI18n();
                    return route.params.locationType === 'warehouse'
                      ? t('modules.replenishment.messages.whReplenishmentProducts')
                      : t('modules.replenishment.messages.storeReplenishmentProducts');
                  },
                  moduleName: ModuleNames.Replenishment,
                },
              },
              {
                path: 'inventories',
                component: () => import('@/app/modules/replenishment/views/replenishment-inventories.vue'),
                props: (route) => ({ locationType: route.params.locationType }),
                meta: {
                  trackVisit: true,
                  title: (route) => {
                    const { t } = useI18n();
                    return route.params.locationType === 'warehouse'
                      ? t('modules.replenishment.messages.whReplenishmentInventories')
                      : t('modules.replenishment.messages.storeReplenishmentInventories');
                  },
                  moduleName: ModuleNames.Replenishment,
                },
              },
            ],
          },
        ],
      },
      {
        path: '/analytics',
        component: () => import('@/app/modules/superset/views/analytics-index.vue'),
        children: [
          {
            path: 'dashboards/:id',
            component: () => import('@/app/modules/superset/views/analytics-dashboard.vue'),
            meta: {
              trackVisit: true,
              title: getSupersetPageTitle,
              moduleName: ModuleNames.Analytics,
            },
          },
        ],
      },
      {
        path: '/planning-assistant',
        component: () => import('@/app/modules/superset/views/analytics-index.vue'),
        meta: {
          guards: [createFeatureFlagGuard(FeatureFlag.AnalyticsPlanningAssistant)],
        },
        children: [
          {
            path: 'dashboards/:id',
            component: () => import('@/app/modules/superset/views/analytics-dashboard.vue'),
            meta: {
              trackVisit: true,
              title: getSupersetPageTitle,
              moduleName: ModuleNames.Analytics,
            },
          },
        ],
      },
      {
        path: '/beta-reports',
        component: () => import('@/app/modules/superset/views/analytics-index.vue'),
        meta: {
          guards: [createFeatureFlagGuard(FeatureFlag.AnalyticsBetaReports)],
        },
        children: [
          {
            path: 'dashboards/:id',
            component: () => import('@/app/modules/superset/views/analytics-dashboard.vue'),
            meta: {
              trackVisit: true,
              title: getSupersetPageTitle,
              moduleName: ModuleNames.Analytics,
            },
          },
        ],
      },
      // Separated routes for new clickhouse based dashboards to not mix with dynamic dashboards logic
      {
        path: '/analytics/inventory-report',
        component: () => import('@/app/modules/analytics/views/analytics-inventory-report.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.inventoryReport');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/analytics/executive-report',
        component: () => import('@/app/modules/analytics/views/analytics-executive-report.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.executiveReport');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/analytics/retail-operations',
        component: () => import('@/app/modules/analytics/views/analytics-retail-operations.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.retailOperations');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/planning-assistant/season-analysis',
        component: () => import('@/app/modules/analytics/views/analytics-season-analysis.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.seasonAnalysis');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/analytics/central-merchandise',
        component: () => import('@/app/modules/analytics/views/analytics-central-merchandise.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.centralMerchandise');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/analytics/store-merchandise',
        component: () => import('@/app/modules/analytics/views/analytics-store-merchandise.vue'),
        meta: {
          guards: [elephantGuard],
          title: () => {
            const { t } = useI18n();
            return t('messages.storeMerchandise');
          },
          moduleName: ModuleNames.Analytics,
        },
      },
      {
        path: '/analytics/replenishment-fulfillment',
        component: () => import('@/app/modules/analytics/views/analytics-replenishment-fulfillment.vue'),
        meta: {
          guards: [elephantGuard],
        },
      },
      {
        path: '/analytics/bi-playground',
        component: () => import('@/app/modules/analytics/views/analytics-bi-playground.vue'),
        meta: {
          guards: [elephantGuard],
        },
      },
      {
        path: '/analytics/pullback-candidates',
        component: () => import('@/app/modules/analytics/views/analytics-pullback-candidates.vue'),
        meta: {
          guards: [elephantGuard],
        },
      },
      {
        path: '/planning-assistant/assortment',
        component: () => import('@/app/modules/analytics/views/analytics-assortment-analysis.vue'),
        meta: {
          guards: [elephantGuard],
        },
      },
      {
        path: '/inventory',
        component: RouterView,
        children: [
          {
            path: '',
            component: () => import('@/app/modules/inventory/views/inventory-index.vue'),
            redirect: '/inventory/location',
            children: [
              {
                name: RoutesNames.InventoryLocations,
                path: 'location',
                component: () => import('@/app/modules/inventory/views/inventory-location.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.inventory.messages.inventoryLocations');
                  },
                  moduleName: ModuleNames.Inventory,
                },
              },
              {
                name: RoutesNames.InventorySkus,
                path: 'sku',
                component: () => import('@/app/modules/inventory/views/inventory-sku.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.inventory.messages.inventorySkus');
                  },
                  moduleName: ModuleNames.Inventory,
                },
              },
              {
                name: RoutesNames.InventoryInventories,
                path: 'inventories',
                component: () => import('@/app/modules/inventory/views/inventory-inventories.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.inventory.messages.inventoryInventories');
                  },
                  moduleName: ModuleNames.Inventory,
                },
              },
              {
                name: RoutesNames.InventoryTransactions,
                path: 'transactions',
                component: () => import('@/app/modules/inventory/views/inventory-transactions.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.inventory.messages.inventoryTransactions');
                  },
                  moduleName: ModuleNames.Inventory,
                },
              },
              {
                name: RoutesNames.InventoryProductLocations,
                path: 'product-locations',
                component: () => import('@/app/modules/inventory/views/inventory-product-locations.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('messages.productLocations');
                  },
                  moduleName: ModuleNames.Inventory,
                },
              },
            ],
          },
          {
            path: 'inventories/:id',
            component: () => import('@/app/modules/sku-chart/views/sku-chart.vue'),
            props: {
              breadcrumbs: ['/inventory', '/inventory/inventories'],
            },
          },
          {
            path: 'location/avoid-replenishment-upload',
            component: () => import('@/app/modules/inventory/views/inventory-avoid-replenishment-upload.vue'),
            props: () => {
              const { t } = useI18n();

              return {
                breadcrumbs: [
                  {
                    label: t('messages.inventory'),
                    href: '/inventory',
                  },
                  {
                    label: t('modules.inventory.messages.breadcrumbLocations'),
                    href: '/inventory/location',
                  },
                  {
                    label: t('modules.inventory.messages.avoidReplenishmentSettingsUploadingPageTitle'),
                    href: '/inventory/location/avoid-replenishment-upload',
                  },
                ],
              };
            },
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('modules.inventory.messages.locationsReplenishmentStatusUpload');
              },
              moduleName: ModuleNames.Inventory,
            },
          },
          {
            path: 'sku/avoid-replenishment-upload',
            component: () => import('@/app/modules/inventory/views/inventory-avoid-replenishment-upload.vue'),
            props: () => {
              const { t } = useI18n();

              return {
                breadcrumbs: [
                  {
                    label: t('messages.inventory'),
                    href: '/inventory',
                  },
                  {
                    label: t('messages.sku'),
                    href: '/inventory/sku',
                  },
                  {
                    label: t('modules.inventory.messages.avoidReplenishmentSettingsUploadingPageTitle'),
                    href: '/inventory/sku/avoid-replenishment-upload',
                  },
                ],
              };
            },
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.skusReplenishmentStatusUpload');
              },
              moduleName: ModuleNames.Inventory,
            },
          },
          {
            path: 'inventories/avoid-replenishment-upload',
            component: () => import('@/app/modules/inventory/views/inventory-avoid-replenishment-upload.vue'),
            props: () => {
              const { t } = useI18n();

              return {
                breadcrumbs: [
                  {
                    label: t('messages.inventory'),
                    href: '/inventory',
                  },
                  {
                    label: t('modules.inventory.messages.breadcrumbInventories'),
                    href: '/inventory/inventories',
                  },
                  {
                    label: t('modules.inventory.messages.avoidReplenishmentSettingsUploadingPageTitle'),
                    href: '/inventory/inventories/avoid-replenishment-upload',
                  },
                ],
              };
            },
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('modules.inventory.messages.inventoriesReplenishmentStatusUpload');
              },
              moduleName: ModuleNames.Inventory,
            },
          },
        ],
      },
      {
        path: '/store-transfer',
        component: RouterView,
        children: [
          {
            path: '',
            component: () => import('@/app/modules/store-transfer/views/store-transfer-index.vue'),
            redirect: '/store-transfer/recommendations',
            children: [
              {
                path: 'recommendations',
                component: () => import('@/app/modules/store-transfer/views/store-transfer-recommendations.vue'),
                redirect: '/store-transfer/recommendations/trips',
                children: [
                  {
                    name: RoutesNames.StoreTransferRecommendationsTrips,
                    path: 'trips',
                    component: () =>
                      import('@/app/modules/store-transfer/views/store-transfer-recommendations-trips.vue'),
                    meta: {
                      trackVisit: true,
                      title: () => {
                        const { t } = useI18n();
                        return t('modules.storeTransfer.messages.storeTransferRecommendationsTrips');
                      },
                      moduleName: ModuleNames.StoreTransfer,
                    },
                  },
                  {
                    name: RoutesNames.StoreTransferRecommendationsProducts,
                    path: 'products',
                    component: () =>
                      import('@/app/modules/store-transfer/views/store-transfer-recommendations-products.vue'),
                    meta: {
                      trackVisit: true,
                      title: () => {
                        const { t } = useI18n();
                        return t('modules.storeTransfer.messages.storeTransferRecommendationsProducts');
                      },
                      moduleName: ModuleNames.StoreTransfer,
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'recommendations/clusters/:clusterId',
            component: () => import('@/app/modules/store-transfer/views/store-transfer-cluster-sku-trips.vue'),
          },
          {
            path: 'analysis',
            component: () => import('@/app/modules/store-transfer/views/store-transfer-analysis.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.transfersResultsAnalysis');
              },
              moduleName: ModuleNames.StoreTransfer,
            },
          },
          {
            path: 'results-analysis',
            component: () => import('@/app/modules/store-transfer/views/store-transfer-analysis-clickhouse.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.transfersResultsAnalysis');
              },
              moduleName: ModuleNames.StoreTransfer,
              guards: [elephantGuard],
            },
          },
        ],
      },
      {
        path: '/special-events',
        component: RouterView,
        children: [
          {
            name: RoutesNames.SpecialEvents,
            path: '',
            component: () => import('@/app/modules/special-events-elephant/views/special-events-entry.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.specialEvents');
              },
              moduleName: ModuleNames.SpecialEvents,
              guards: [specialEventsEntryGuard],
            },
            children: [
              {
                name: RoutesNames.SpecialEventsPending,
                path: 'pending',
                component: () => import('@/app/modules/special-events/views/special-events-pending.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.specialEvents.messages.pendingSpecialEvents');
                  },
                  moduleName: ModuleNames.SpecialEvents,
                  guards: [legacySpecialEventsGuard],
                },
              },
              {
                name: RoutesNames.SpecialEventsUpcoming,
                path: 'upcoming',
                component: () => import('@/app/modules/special-events/views/special-events-upcoming.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.specialEvents.messages.upcomingSpecialEvents');
                  },
                  moduleName: ModuleNames.SpecialEvents,
                  guards: [legacySpecialEventsGuard],
                },
              },
              {
                name: RoutesNames.SpecialEventsApproved,
                path: 'approved',
                component: () => import('@/app/modules/special-events/views/special-events-approved.vue'),
                meta: {
                  trackVisit: true,
                  title: () => {
                    const { t } = useI18n();
                    return t('modules.specialEvents.messages.approvedSpecialEvents');
                  },
                  moduleName: ModuleNames.SpecialEvents,
                  guards: [legacySpecialEventsGuard],
                },
              },
            ],
          },
          {
            name: RoutesNames.SpecialEventsAnalysis,
            path: 'analysis',
            component: () => import('@/app/modules/special-events/views/special-events-analysis.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('globalNavigation.preEventsAnalysis');
              },
              moduleName: ModuleNames.SpecialEvents,
            },
          },
          {
            name: RoutesNames.SpecialEventsUploadPage,
            path: 'uploads/:id',
            component: () => import('@/app/modules/special-events-elephant-v2/views/special-event-upload-page.vue'),
            props: (route) => ({ id: route.params.id }),
            meta: {
              title: () => {
                const { t } = useI18n();
                return t('messages.specialEvents');
              },
              moduleName: ModuleNames.SpecialEvents,
              guards: [createFeatureFlagGuard(FeatureFlag.SpecialEventsElephantV2, '/special-events'), elephantGuard],
            },
          },
          {
            path: ':id',
            component: () => import('@/app/modules/special-events/views/special-event.vue'),
            meta: {
              guards: [legacySpecialEventsGuard],
            },
          },
          {
            path: ':id/analysis',
            component: () => import('@/app/modules/special-events/views/special-events-analysis.vue'),
            props: (route) => ({ id: route.params.id }),
            meta: {
              guards: [legacySpecialEventsGuard],
            },
          },
          {
            name: RoutesNames.SpecialEventsUpload,
            path: 'upload',
            component: () => import('@/app/modules/special-events/views/special-events-upload.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('specialEvents.messages.uploadSpecialEvents');
              },
              moduleName: ModuleNames.SpecialEvents,
              guards: [legacySpecialEventsGuard],
            },
          },
        ],
      },
      {
        path: '/sku-chart/:id',
        component: () => import('@/app/modules/sku-chart/views/sku-chart.vue'),
      },
      {
        path: '/data-center',
        component: () => import('@/app/modules/data-center/views/data-center-index-entry.vue'),
        redirect: '/data-center/downloads',
        children: [
          {
            name: RoutesNames.DataCenterUploads,
            path: 'uploads',
            component: () => import('@/app/modules/data-center/views/data-center-uploads.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.uploads');
              },
              moduleName: ModuleNames.DataCenter,
              guards: [createFeatureFlagGuard(FeatureFlag.DataCenterUploads)],
            },
          },
          {
            name: RoutesNames.DataCenterDownloads,
            path: 'downloads',
            component: () => import('@/app/modules/data-center/views/data-center-downloads.vue'),
            meta: {
              trackVisit: true,
              title: () => {
                const { t } = useI18n();
                return t('messages.downloads');
              },
              moduleName: ModuleNames.DataCenter,
            },
          },
        ],
      },
      {
        name: RoutesNames.InventoryManagement,
        path: '/inventory-management',
        component: () => import('@/app/modules/inventory-management/views/inventory-management-react.vue'),
        meta: {
          title: () => {
            const { t } = useI18n();
            return t('globalNavigation.inventoryManagement');
          },
          guards: [createFeatureFlagGuard(FeatureFlag.InventoryManagement), elephantGuard],
        },
      },
      {
        path: '/allocations',
        component: () => import('@/app/modules/allocations/views/allocations-index.vue'),
        children: [
          {
            path: '',
            component: () => import('@/app/modules/allocations/views/allocations-groups.vue'),
            meta: {
              moduleName: ModuleNames.Allocations,
              title: () => {
                const { t } = useI18n();
                return t('globalNavigation.allocations');
              },
              trackVisit: true,
            },
          },
          {
            path: 'upload/',
            component: () => import('@/app/modules/allocations/views/allocations-upload.vue'),
            meta: {
              moduleName: ModuleNames.Allocations,
              title: () => {
                const { t } = useI18n();
                return t('messages.uploads');
              },
              trackVisit: true,
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'uploading/:id',
            component: () => import('@/app/modules/allocations/views/allocations-uploading.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'create-group',
            component: () => import('@/app/modules/allocations/views/allocations-group-creation.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'groups/:id',
            component: () => import('@/app/modules/allocations/views/allocations-group.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'approved',
            component: () => import('@/app/modules/allocations/views/allocations-approved.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'pending',
            component: () => import('@/app/modules/allocations/views/allocations-pending.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
          {
            path: 'comparable-products/:id',
            component: () => import('@/app/modules/allocations/views/allocations-comparable-products.vue'),
            meta: {
              layoutFeatures: {
                sidebar: false,
              },
            },
          },
        ],
        meta: {
          guards: [createFeatureFlagGuard(FeatureFlag.AssortmentAllocations, '/')],
        },
      },
    ],
  },
  {
    path: '/auth',
    name: 'auth',
    meta: {
      guards: [notAuthGuard],
    },
    component: () => import('@/app/views/auth-page.vue'),
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('@/app/views/not-found.vue'),
  },
];
